import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../services/adminService';
import type { WhatsAppSettings as WhatsAppSettingsType } from '../../services/adminService';

/**
 * WhatsAppSettings — Admin page for managing WhatsApp integration settings.
 *
 * Requirement 7.2 — Enable/disable toggle for WhatsApp integration.
 * Requirement 7.3 — Credential input fields shown when enabled.
 * Requirement 7.4 — Credential fields hidden when disabled.
 * Requirement 7.5 — Save settings via PUT /admin/settings/whatsapp.
 * Requirement 7.6 — Fetch and pre-populate settings on mount.
 * Requirement 7.7 — Status indicator for configuration validity.
 */

const WhatsAppSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [verifyToken, setVerifyToken] = useState('');

  // Track whether the user has modified the access token field
  const [accessTokenModified, setAccessTokenModified] = useState(false);
  // Store the original masked token from the GET response
  const [maskedAccessToken, setMaskedAccessToken] = useState('');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings: WhatsAppSettingsType = await adminService.getWhatsAppSettings();
      setEnabled(settings.enabled);
      setPhoneNumberId(settings.phoneNumberId || '');
      setAccessToken(settings.accessToken || '');
      setMaskedAccessToken(settings.accessToken || '');
      setVerifyToken(settings.verifyToken || '');
      setAccessTokenModified(false);
    } catch (err) {
      setError('Failed to load WhatsApp settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleAccessTokenChange = (value: string) => {
    setAccessToken(value);
    setAccessTokenModified(true);
  };

  const isConfigured = enabled && phoneNumberId.trim() !== '' &&
    ((accessTokenModified && accessToken.trim() !== '') || (!accessTokenModified && maskedAccessToken !== '')) &&
    verifyToken.trim() !== '';

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      await adminService.updateWhatsAppSettings({
        enabled,
        phoneNumberId,
        accessToken: accessTokenModified ? accessToken : maskedAccessToken,
        verifyToken,
      });
      setSaveSuccess('WhatsApp settings saved successfully.');
      // Re-fetch to get updated masked token
      await fetchSettings();
    } catch (err) {
      setSaveError('Failed to save WhatsApp settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="whatsapp-settings" data-testid="whatsapp-settings-loading">
        <h2>WhatsApp Settings</h2>
        <p>Loading settings…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="whatsapp-settings" data-testid="whatsapp-settings-error">
        <h2>WhatsApp Settings</h2>
        <p className="error-message">{error}</p>
        <button onClick={fetchSettings}>Retry</button>
      </div>
    );
  }

  return (
    <div className="whatsapp-settings" data-testid="whatsapp-settings">
      <h2>WhatsApp Settings</h2>

      <div className="whatsapp-status" data-testid="whatsapp-status">
        {isConfigured ? (
          <span className="status-badge status-connected" data-testid="whatsapp-status-connected">
            Connected
          </span>
        ) : (
          <span className="status-badge status-not-configured" data-testid="whatsapp-status-not-configured">
            Not Configured
          </span>
        )}
      </div>

      <div className="whatsapp-toggle" data-testid="whatsapp-toggle">
        <label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="whatsapp-enabled-toggle"
          />
          Enable WhatsApp Integration
        </label>
      </div>

      {enabled && (
        <div className="whatsapp-credentials" data-testid="whatsapp-credentials">
          <div className="form-group">
            <label htmlFor="phoneNumberId">Phone Number ID</label>
            <input
              id="phoneNumberId"
              type="text"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              placeholder="Enter Meta Phone Number ID"
              data-testid="whatsapp-phone-number-id"
            />
          </div>

          <div className="form-group">
            <label htmlFor="accessToken">Access Token</label>
            <input
              id="accessToken"
              type="password"
              value={accessToken}
              onChange={(e) => handleAccessTokenChange(e.target.value)}
              placeholder={maskedAccessToken ? 'Enter new token to replace current' : 'Enter Meta Access Token'}
              data-testid="whatsapp-access-token"
            />
          </div>

          <div className="form-group">
            <label htmlFor="verifyToken">Verify Token</label>
            <input
              id="verifyToken"
              type="text"
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value)}
              placeholder="Enter webhook verify token"
              data-testid="whatsapp-verify-token"
            />
          </div>
        </div>
      )}

      {saveSuccess && (
        <p className="success-message" data-testid="whatsapp-save-success">{saveSuccess}</p>
      )}
      {saveError && (
        <p className="error-message" data-testid="whatsapp-save-error">{saveError}</p>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        data-testid="whatsapp-save-button"
        className="save-button"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

export default WhatsAppSettings;
